import type { TransferProps } from "antd";
import { Button, Card, Input, Modal, Select, Spin, Table, Tooltip, Transfer } from "antd";
import { TReportUserParameters, TReportUsers } from "api/services/models/types";
import {
  getReportForAccount,
  getReportForAccountAndUser,
  getReportUsers,
  saveReportAccessForUser
} from "api/services/ReportService";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import React, { useEffect, useState } from "react";
import { usePicklists } from "../picklists/PicklistContextProvider";

interface ManageReportAccessProps {
  isVisible: boolean;
  handleClose: () => void;
}

interface RecordType {
  key: string;
  title: string;
}

const ManageReportAccess: React.FC<ManageReportAccessProps> = ({ isVisible, handleClose }) => {
  const [userItems, setUserItems] = useState<TReportUsers[]>([]);
  const [loadingCard1, setLoadingCard1] = useState(true);
  const [loadingCard2, setLoadingCard2] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string>("");
  const [UserName, setUserName] = useState<string>("");
  const [location, setLocation] = useState<number | undefined>(undefined);
  const [sourceData, setSourceData] = useState<RecordType[]>([]);
  const [targetKeys, setTargetKeys] = useState<TransferProps["targetKeys"]>([]);
  const { accountId, webDUser } = useTDAuthentication();
  const { locations } = usePicklists();

  useEffect(() => {
    if (isVisible) {
      fetchData();
    }
  }, [isVisible]);

  const handleChange: TransferProps["onChange"] = newTargetKeys => {
    setTargetKeys(newTargetKeys);
  };

  const resetStates = () => {
    setUserItems([]);
    setLoadingCard1(true);
    setLoadingCard2(true);
    setSelectedUserId(null);
    setUserId("");
    setUserName("");
    setLocation(undefined);
    setSourceData([]);
    setTargetKeys([]);
  };

  const handleModalClose = () => {
    resetStates();
    handleClose();
  };

  const fetchData = async () => {
    setLoadingCard1(true);
    setLoadingCard2(true);
    const reportParameters: TReportUserParameters = {
      userId: userId,
      userName: UserName,
      locationId: location
    };
    const fetchedUsers = await getReportUsers(
      accountId || "",
      webDUser?.user.userId || "",
      reportParameters
    );
    const accountReports = await getReportForAccount(
      accountId || "",
      webDUser?.user.userId || ""
    );
    const data = accountReports.map<RecordType>((item, i) => ({
      key: item.toString(),
      title: item.toString()
    }));
    setUserItems(fetchedUsers);
    setSourceData(data);
    setLoadingCard1(false);
    setLoadingCard2(false);
  };

  const handleRowClick = async (userId: string) => {
    setSelectedUserId(userId);
    const result = await getReportForAccountAndUser(
      accountId || "",
      webDUser?.user.userId || "",
      userId
    );
    //@ts-ignore
    const tempMockData = [];
    result.forEach(item => {
      tempMockData.push(item.toString());
    });
    //@ts-ignore
    setTargetKeys(tempMockData);
  };

  const handleSearch = () => {
    console.log("Searching with:", { userId, UserName, location });
    setSelectedUserId(null);
    fetchData();
  };

  const handleSave = async () => {
    await saveReportAccessForUser(
      accountId || "",
      webDUser?.user.userId || "",
      selectedUserId || "",
      (targetKeys ?? []).map(key => key.toString())
    );
    handleModalClose();
  };

  const columns = [
    {
      title: "User ID",
      dataIndex: "userId",
      key: "userId",
      render: (text: string, record: TReportUsers) => (
        <Tooltip title={record?.isProvider ? "Provider" : "User"}>
          <span>{text}</span>
        </Tooltip>
      )
    },
    {
      title: "Name",
      dataIndex: "firstname",
      key: "firstname",
      render: (text: any, record: TReportUsers) => (
        <Tooltip title={record.isProvider ? "Provider" : "User"}>
          <span>{`${record?.firstName} ${record?.lastName}`}</span>
        </Tooltip>
      )
    }
  ];

  return (
    <Modal
      title="Manage Report Access"
      open={isVisible}
      onCancel={handleModalClose}
      footer={null}
      width={1000}
      centered={true}>
      <Card style={{ marginBottom: "16px", marginRight: "8px" }}>
        <div style={{ display: "flex", justifyContent: "space-around", alignItems: "end" }} className="flex-container">
          <div
            style={{
              flex: 1
            }}>
            <label
              htmlFor="userID"
              style={{
                fontSize: "12px",
                display: "block",
                marginBottom: "4px"
              }}>
              User ID
            </label>
            <Input
              id="userId"
              placeholder="User ID"
              value={userId}
              onChange={e => setUserId(e.target.value)}
              style={{ width: "90%" }}
            />
          </div>
          <div
            style={{
              flex: 1
            }}>
            <label
              htmlFor="UserName"
              style={{
                fontSize: "12px",
                display: "block",
                marginBottom: "4px"
              }}>
              Contact Name
            </label>
            <Input
              id="UserName"
              placeholder="Contact Name"
              value={UserName}
              onChange={e => setUserName(e.target.value)}
              style={{ width: "90%" }}
            />
          </div>
          <div
            style={{
              flex: 1,
              marginRight: "12px"
            }}>
            <label
              htmlFor="location"
              style={{
                fontSize: "12px",
                display: "block",
                marginBottom: "4px"
              }}>
              Location
            </label>
            <Select
              id="location"
              allowClear
              variant="borderless"
              style={{
                width: "100%",
                border: "1px solid #7777774D",
                borderRadius: "12px"
              }}
              placeholder="Choose location"
              maxTagCount="responsive"
              showSearch
              onChange={value => setLocation(value)}>
              <Select.Option key={0} value={null}>
                All Locations
              </Select.Option>
              {locations?.map(location => (
                <Select.Option key={location.locationId} value={location.locationId}>
                  {location.locationName}
                </Select.Option>
              ))}
            </Select>
          </div>
          <Button type="primary" onClick={handleSearch}>
            Search
          </Button>
        </div>
      </Card>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Card style={{ marginBottom: "16px", flex: 1, marginRight: "8px" }} styles={{body:{padding:"0"}}} className="card-style">
          {loadingCard1 && <Spin />}
          {!loadingCard1 && userItems && userItems.length > 0 && (
            <Table
              dataSource={userItems}
              style={{ border: "none" }}
              className="table-style"
              components={{
                body: {
                  cell: (props: any) => (
                    <td
                      {...props}
                      style={{
                        borderBottomWidth: "1px",
                        borderBottomColor: "#f0f0f0",
                        borderBottomStyle: "solid"
                      }}
                    />
                  )
                }
              }}
              columns={columns}
              rowKey="userId"
              pagination={false}
              scroll={{ y: 600 }}
              onRow={record => ({
                onClick: () => void handleRowClick(record.userId),
                style: {
                  cursor: "pointer",
                  backgroundColor: selectedUserId === record.userId ? "#e6f7ff" : ""
                }
              })}
            />
          )}
          {!loadingCard1 && (!userItems || userItems.length === 0) && (
            <div style={{ textAlign: "center", padding: "20px" }}>No users available.</div>
          )}
        </Card>

        <Card style={{ marginBottom: "16px", flex: 1}} styles={{body:{padding:"2rem"}}} className="card-style">
          {loadingCard2 && <Spin />}
          {!loadingCard2 && sourceData && sourceData.length > 0 && (
            <>
              <Transfer
                titles={["Available Reports", "Assigned Reports"]}
                onChange={handleChange}
                render={item => <span>{item.title}</span>}
                dataSource={sourceData}
                targetKeys={targetKeys}
                disabled={selectedUserId === null}
                listStyle={{
                  width: 400,
                  height: 300
                }}
                style={{ display: "flex", flexDirection: "column" }}
              />

              <Button
                type="primary"
                style={{ margin: "12px", display: "block", marginLeft: "auto" }}
                onClick={handleSave}>
                Save
              </Button>
            </>
          )}
          {!loadingCard2 && (!sourceData || sourceData.length === 0) && (
            <div style={{ textAlign: "center", padding: "20px" }}>No reports available.</div>
          )}
        </Card>
      </div>
    </Modal>
  );
};

export default ManageReportAccess;
